import React, { useState, useEffect, useContext } from 'react';
import { CSSProperties } from 'react';
import useBrand from 'Hooks/useBrand';
import useMessageLimits from 'Hooks/useMessageLimits';
import useMaestroChat from 'Hooks/useMaestroChat';
import { Body3, Subtitle1, Body2 } from 'components/shareable/Typography';
import clsx from 'clsx';
import TagSymphony from 'elements/TagSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import { styx } from 'modules/Utils';
import TaskPackPurchaseModal from 'components/TaskPackPurchaseModal/TaskPackPurchaseModal';
import { useAuth0 } from '@auth0/auth0-react';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import { PlanType } from 'modules/Const';

interface MaestroMessageLimitCounterProps {
    className?: string;
    showUpgradeHint?: boolean;
}

const MaestroMessageLimitCounter: React.FC<MaestroMessageLimitCounterProps> = ({
    className,
    showUpgradeHint = false
}) => {
    const { isAuthenticated } = useAuth0();
    const { currentBrand, isPaidBrand } = useBrand();
    const { activeWorkspace, isChatOpen } = useMaestroChat();
    const {
        messageUsage,
        isLoadingUsage,
        hasUsageError,
        getMessagesRemaining,
        getNeedsUpgrade,
        setIsEnforcementModalOpen
    } = useMessageLimits();

    const { handleOpenModal } = useContext(UpgradeModalContext);

    const [showActionExplanation, setShowActionExplanation] = useState(false);
    const [taskPackModalOpen, setTaskPackModalOpen] = useState(false);

    // Cache state to track loading history and last known values
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [lastKnownMessagesRemaining, setLastKnownMessagesRemaining] = useState<number | null>(null);

    // Helper function to get contextual upgrade text based on workspace state
    const getContextualUpgradeText = () => {
        const workspaceTypes = ['campaignPreview', 'contentAnalysis', 'websiteBuilder', 'marketingPlan'];
        const hasActiveWorkspace = activeWorkspace && workspaceTypes.includes(activeWorkspace.type);

        if (hasActiveWorkspace && isChatOpen) {
            return 'Upgrade to start new chats';
        }

        return 'Upgrade for more Messages';
    };

    // Update cache when message usage changes
    useEffect(() => {
        if (messageUsage) {
            setHasLoadedOnce(true);
            setLastKnownMessagesRemaining(getMessagesRemaining());
        }
    }, [messageUsage]);

    const handleTagClick = () => {
        const messagesRemaining = getMessagesRemaining();
        if (messagesRemaining > 0) {
            setShowActionExplanation(true);
        } else {
            if (!isAuthenticated) {
                setIsEnforcementModalOpen(true);
            } else {
                handleOpenModal({
                    source: 'message_limit_enforcement',
                    highlightedPlan: PlanType.GROWTH,
                    upgradeCallToAction: 'Upgrade to Growth to unlock the full power of Maestro.'
                });
            }
        }
    };

    const renderMessageTag = (messagesRemaining: number) => {
        let displayText = '';
        let colorClass = '';

        if (!isAuthenticated) {
            if (getNeedsUpgrade()) {
                displayText = 'No Messages Left';
                colorClass = 'taskCounterError';
            } else if (messagesRemaining <= 10) {
                displayText = `${messagesRemaining} Message${messagesRemaining === 1 ? '' : 's'} Remaining`;
                colorClass = 'taskCounterWarning';
            } else {
                displayText = `${messagesRemaining} Messages Remaining`;
                colorClass = 'taskCounterNormal';
            }
        } else {

            if (getNeedsUpgrade()) {
                displayText = 'No Messages Left';
                colorClass = 'taskCounterError';
            } else if (messagesRemaining <= 10) {
                displayText = `${messagesRemaining} Message${messagesRemaining === 1 ? '' : 's'} Remaining`;
                colorClass = 'taskCounterWarning';
            } else {
                displayText = `${messagesRemaining} Messages Remaining`;
                colorClass = 'taskCounterNormal';
            }
        }

        if (showUpgradeHint && getNeedsUpgrade() && !isPaidBrand) {
            displayText = getContextualUpgradeText();
        }

        return (
            <div
                className={clsx('maestroMessageLimitCounter', className, colorClass)}
                style={sty.container}
            >
                <TagSymphony
                    className="messageLimitCounterTag"
                    style={styx(sty.tag, sty.clickableTag)}
                    color={colorClass}
                    title={displayText}
                    onClick={handleTagClick}
                />
            </div>
        );
    };

    const renderActionExplanation = () => {
        return (
            <ModalSymphony
                title="What are AI Messages?"
                subtitle="Each message turn between you and Maestro counts toward your monthly message limit. Your first message won't count toward your limit."
                className="messageExplanation"
                isOpen={showActionExplanation}
                onClose={() => setShowActionExplanation(false)}
            >
                <div className="messageExplanationContent" style={sty.modalContent}>
                    <div className="messageTypesList">
                        <Body3 style={{ fontWeight: 600, marginBottom: 8 }}>Messages include:</Body3>
                        <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                            <li>Questions about your marketing strategy</li>
                            <li>Build ad campaigns via chat</li>
                            <li>Content analysis and recommendations</li>
                            <li>Build exportable marketing plans and reports</li>
                            <li>And more!</li>
                        </ul>
                    </div>

                    {messageUsage?.reset_date && (
                        <Body2 style={{ marginTop: 16, fontWeight: 500 }}>
                            Your message limit resets on {new Date(messageUsage.reset_date).toLocaleDateString()}. Upgrade to get more messages and unlock Maestro's full potential for unlimited conversations.
                        </Body2>
                    )}
                </div>
            </ModalSymphony>
        );
    };

    // Helper function to determine what to show
    const getDisplayState = () => {
        // Show error state
        if (hasUsageError) {
            return 'error';
        }

        // Show loading only if we haven't loaded once and don't have cached data
        if (!hasLoadedOnce && !lastKnownMessagesRemaining) {
            return 'loading';
        }

        // Otherwise show the message counter
        return 'counter';
    };

    // Helper function to get messages remaining with fallback to cached value
    const getMessagesRemainingWithCache = () => {
        if (lastKnownMessagesRemaining !== null && (!messageUsage || isLoadingUsage)) {
            return lastKnownMessagesRemaining;
        }
        return getMessagesRemaining();
    };

    const displayState = getDisplayState();

    if (displayState === 'loading') {
        return (
            <div
                className={clsx('maestroMessageLimitCounter', 'maestroMessageLimitCounterLoading', className)}
                style={sty.container}
            >
                <Body3
                    className="messageLimitCounterText"
                    style={sty.text}
                >
                    Loading messages...
                </Body3>
            </div>
        );
    }

    if (displayState === 'error') {
        return (
            <div
                className={clsx('maestroMessageLimitCounter', 'maestroMessageLimitCounterError', className)}
                style={sty.container}
            >
                <Body3
                    className="messageLimitCounterText"
                    style={sty.text}
                >
                    Error loading messages
                </Body3>
            </div>
        );
    }

    const messagesRemaining = getMessagesRemainingWithCache();

    return (
        <>
            {renderMessageTag(messagesRemaining)}
            {renderActionExplanation()}
            {taskPackModalOpen && (
                <TaskPackPurchaseModal
                    isOpen={taskPackModalOpen}
                    onClose={() => setTaskPackModalOpen(false)}
                />
            )}
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: 4
    },
    text: {
        fontSize: 12,
        fontWeight: 500,
        color: '#333',
    },
    tag: {
        // Base tag styles
    },
    clickableTag: {
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
    },
    explanationText: {
        lineHeight: '1.5',
        marginBottom: 0,
    }
};

export default MaestroMessageLimitCounter; 